import { useEffect, useRef } from "react";

// Auto-fit a heading to a single line on wide screens; wraps normally on mobile.
export const useFitText = (text, { minPx = 26, maxPx = 132, bp = 1024 } = {}) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      if (window.innerWidth < bp) {
        el.style.fontSize = "";
        el.style.whiteSpace = "";
        return;
      }
      el.style.whiteSpace = "nowrap";
      el.style.fontSize = "100px";
      const avail = el.clientWidth;
      const natural = el.scrollWidth;
      if (avail > 0 && natural > 0) {
        const size = Math.max(minPx, Math.min((avail / natural) * 100, maxPx));
        el.style.fontSize = `${size}px`;
      }
    };
    fit();
    const band = el.closest("[data-fit-band]") || el.parentElement;
    const ro = new ResizeObserver(fit);
    if (band) ro.observe(band);
    window.addEventListener("resize", fit);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [text, minPx, maxPx, bp]);
  return ref;
};

// Register/crop marks that overshoot each corner (Nordic editorial look).
export const CropCorners = ({ mark = "bg-[color:var(--s-ink)]/30" }) => {
  const l = `pointer-events-none absolute ${mark}`;
  return (
    <>
      <span aria-hidden className={`${l} -left-2 top-0 h-px w-6`} />
      <span aria-hidden className={`${l} left-0 -top-2 h-6 w-px`} />
      <span aria-hidden className={`${l} -right-2 top-0 h-px w-6`} />
      <span aria-hidden className={`${l} right-0 -top-2 h-6 w-px`} />
      <span aria-hidden className={`${l} -left-2 bottom-0 h-px w-6`} />
      <span aria-hidden className={`${l} left-0 -bottom-2 h-6 w-px`} />
      <span aria-hidden className={`${l} -right-2 bottom-0 h-px w-6`} />
      <span aria-hidden className={`${l} right-0 -bottom-2 h-6 w-px`} />
    </>
  );
};

// Nordic editorial title: small kicker eyebrow + oversized bold-uppercase display heading (no box).
export const TitleBand = ({
  eyebrow,
  heading,
  dark = false,
  tone,
  align = "left",
  className = "",
  index,
  bandTestId,
  eyebrowTestId,
  headingTestId,
}) => {
  const isDark = dark || tone === "dark";
  const alignCls = align === "center" ? "text-center" : align === "right" ? "text-left lg:text-right" : "text-left";
  const alignRow = align === "center" ? "justify-center" : align === "right" ? "lg:justify-end" : "";
  return (
    <div className={`s-reveal min-w-0 ${alignCls} ${className}`} data-testid={bandTestId}>
      {(eyebrow || index) ? (
        <div
          className={`s-micro mb-4 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] ${alignRow} ${
            isDark ? "text-[color:var(--s-gold-bright)]" : "text-[color:var(--s-gold)]"
          }`}
          data-testid={eyebrowTestId}
        >
          {index ? <span className="opacity-60">({String(index).padStart(2, "0")})</span> : null}
          {eyebrow ? <span>{eyebrow}</span> : null}
        </div>
      ) : null}
      <h2
        className={`s-h text-4xl uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl ${
          isDark ? "text-white" : "text-[color:var(--s-ink)]"
        }`}
        data-testid={headingTestId}
      >
        {heading}
      </h2>
    </div>
  );
};
